import { formatDate } from '@/shared/lib/dateUtils'
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom'; // 👈 Se agrega useLocation
import { Bell, ExternalLink } from 'lucide-react';
import { useSupplierNotifications } from '@/context/SupplierNotificationsContext';
import { type ApiNotification } from '@/services/notificationsService';

const SupplierNotificationBell = () => {

    const { notifications, unreadCount, markAsRead } = useSupplierNotifications();
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all');
    const [selectedId, _setSelectedId] = useState<string | null>(null);


    const dropdownRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const location = useLocation(); // 👈 Hook para detectar la URL actual

    // 👇 Definimos la ruta del panel de proveedores
    const panelPath = '/proveedores/notificaciones';
    // 👇 Determinamos si el icono debe estar en azul
    const isBellActive = location.pathname === panelPath;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleToggle = () => setIsOpen(!isOpen);

    const handleNotificationClick = async (notification: ApiNotification) => {
        // 1. Marcar como leída si es necesario
        if (!notification.isRead) {
            await markAsRead(notification.id);
        }

        // 2. Cerrar el dropdown
        setIsOpen(false);

        // 3. SIEMPRE navegar al panel de notificaciones del proveedor
        navigate(panelPath);
    };

    const handleOpenFullPanel = () => {
        setIsOpen(false);
        navigate(panelPath);
    };


    const filteredNotifications = activeTab === 'all'
        ? notifications
        : notifications.filter((n: ApiNotification) => !n.isRead);

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Campana */}
            <button
                onClick={handleToggle}
                className={`relative p-2 rounded-lg transition-colors focus:outline-none ${isBellActive
                    ? 'text-white bg-[#315FB9]'
                    : 'text-[#404040] hover:bg-[#EBEBEB]'
                    }`}
            >
                <Bell className="w-6 h-6" />

                {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 flex items-center justify-center">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-60"></span>
                        <span className="relative flex items-center justify-center rounded-full min-w-[18px] h-[18px] px-1 bg-red-500 border-2 border-white text-[10px] font-bold text-white leading-none">
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-3 w-80 sm:w-96 origin-top-right rounded-xl border border-gray-200 bg-white shadow-2xl z-50 overflow-hidden ring-1 ring-black ring-opacity-5">

                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                        <h3 className="text-lg font-semibold text-gray-900">Notificaciones</h3>

                        <button
                            onClick={handleOpenFullPanel}
                            className="p-1.5 text-[#315FB9] hover:text-[#315FB9] hover:bg-blue-50 rounded-md transition-colors"
                        >
                            <ExternalLink className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="flex p-2 gap-2 border-b border-gray-100">
                        <button
                            onClick={() => setActiveTab('all')}
                            className={`flex-1 py-2 text-sm font-medium transition-all duration-200 rounded-sm ${activeTab === 'all'
                                ? 'bg-[#EBEBEB] text-[#404040] shadow-sm'
                                : 'text-[#404040] hover:bg-[#EBEBEB]'
                                }`}
                        >
                            Todas
                        </button>

                        <button
                            onClick={() => setActiveTab('unread')}
                            className={`flex-1 py-2 text-sm font-medium transition-all duration-200 rounded-sm flex items-center justify-center ${activeTab === 'unread'
                                ? 'bg-[#EBEBEB] text-[#404040] shadow-sm'
                                : 'text-[#404040] hover:bg-[#EBEBEB]'
                                }`}
                        >
                            Sin leer {unreadCount > 0 && `(${unreadCount})`}
                        </button>

                    </div>

                    {/* Lista */}
                    <div className="max-h-[28rem] overflow-y-auto bg-white">
                        {filteredNotifications.length === 0 ? (
                            <div className="p-10 text-center flex flex-col items-center justify-center text-gray-500">
                                <Bell className="w-10 h-10 text-gray-200 mb-3" />
                                <p className="text-sm font-medium text-gray-900">
                                    {activeTab === 'all'
                                        ? 'Sin notificaciones'
                                        : 'No tienes mensajes sin leer'}
                                </p>
                            </div>
                        ) : (
                            <ul className="divide-y divide-gray-100">
                                {filteredNotifications.map((notif: ApiNotification) => (
                                    <li
                                        key={notif.id}

                                        onClick={() => handleNotificationClick(notif)}
                                        className={`flex cursor-pointer items-start gap-4 p-4 transition-colors duration-200
                                            ${selectedId === notif.id ? 'bg-[#EBEBEB]' : (!notif.isRead ? 'bg-[#F4F7FB] hover:bg-[#EBEBEB]' : 'bg-white hover:bg-[#EBEBEB]')}
                                        `}
                                    >


                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm font-medium mb-0.5 ${!notif.isRead ? 'text-gray-900' : 'text-gray-600'
                                                }`}>
                                                {notif.title}
                                            </p>

                                            <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                                                {notif.message}
                                            </p>

                                            <p className="text-[10px] text-gray-400 mt-2">
                                                {formatDate(notif.createdAt)} •{' '}
                                                {new Date(notif.createdAt).toLocaleTimeString('es-MX', {
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </p>
                                        </div>

                                        {!notif.isRead && (
                                            <div className="mt-2 h-2 w-2 rounded-full  flex-shrink-0 shadow-sm" />
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                </div>
            )}
        </div>
    );
};

export default SupplierNotificationBell;